import { useState, useCallback, useRef, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { searchCache } from '@/services/search-cache';
import { getLogger } from '@/utils/logger';
import { useToast } from '@/hooks/use-toast';
import { useContextMemory } from '@/hooks/useContextMemory';
import { useAuth } from '@/contexts/AuthContext';

const log = getLogger({ module: 'ContextSearch' });

interface SearchPlan {
  q?: string;
  mood?: string;
  domains?: string[];
  content_mix?: Record<string, number>;
  filters?: Record<string, any>;
  geo?: {
    type: 'city' | 'none';
    name?: string;
    radius_km?: number;
  };
  freshness_hours?: number;
  vibe?: string;
  weights?: {
    semantic: number;
    ts?: number;
    vibe?: number;
    freshness?: number;
    resurface?: number;
  };
  explore?: number;
}

interface SearchResults {
  results: any[];
  total_results: number;
  constellation_id: string | null;
  plan_used: SearchPlan;
  processing_time: number;
}

export const useContextSearch = () => {
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState<SearchResults | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const { user } = useAuth();
  const { trackSearchQuery, trackContentInteraction } = useContextMemory(user?.id);

  // Throttling and batching
  const searchTimeoutRef = useRef<NodeJS.Timeout>();
  const lastSearchRef = useRef<string>('');
  const searchCountRef = useRef(0);

  // Rate limiting
  const rateLimitRef = useRef({ count: 0, resetTime: 0 });
  const MAX_SEARCHES_PER_MINUTE = 10;

  // Heuristic planner (fallback when LLM planner isn't available)
  const buildPlanHeuristic = useCallback((query: string): SearchPlan => {
    const lowerQuery = query.toLowerCase();

    // Detect content type preferences and communities
    const contentTypes = {
      profiles: ['artist', 'designer', 'photographer', 'freelancer', 'creator', 'developer', 'musician'],
      artwork: ['art', 'artwork', 'painting', 'drawing', 'illustration'],
      photos: ['photo', 'photography', 'picture', 'image'],
      stories: ['story', 'stories', 'experience', 'journey'],
      updates: ['update', 'news', 'latest', 'recent'],
      // FOMO official communities
      art: ['art', 'artist', 'artistic', 'creative'],
      photography: ['photo', 'photography', 'photographer'],
      '3d': ['3d', 'modeling', 'render', 'blender'],
      anime: ['anime', 'manga', 'otaku'],
      cinema: ['movie', 'film', 'cinema'],
      gaming: ['game', 'gaming', 'gamer']
    };

    const moodKeywords = {
      happy: ['happy', 'joy', 'excited', 'cheerful', 'positive'],
      sad: ['sad', 'down', 'blue', 'melancholy', 'somber'],
      motivated: ['motivated', 'inspired', 'energy', 'pumped', 'driven'],
      calm: ['calm', 'peaceful', 'zen', 'serene', 'quiet'],
      creative: ['creative', 'artistic', 'innovative', 'imaginative'],
      nostalgic: ['nostalgic', 'memories', 'past', 'vintage', 'classic']
    };

    let detectedMood: string | undefined;
    for (const [mood, keywords] of Object.entries(moodKeywords)) {
      if (keywords.some(keyword => lowerQuery.includes(keyword))) {
        detectedMood = mood;
        break;
      }
    }

    // Detect location intent and lightweight city extraction
    const locationKeywords = ['near me', 'in my area', 'local', 'nearby'];
    const hasLocationIntent = locationKeywords.some(keyword => lowerQuery.includes(keyword));

    // Lightweight city detection from common cities
    const knownCities = [
      'chicago','new york','nyc','los angeles','la','san francisco','sf','austin','miami','seattle','london','paris','berlin','toronto'
    ];
    let detectedCity: string | undefined;
    for (const city of knownCities) {
      if (lowerQuery.includes(city)) {
        // Normalize a few common abbreviations
        const normalized = city === 'nyc' ? 'New York' : city === 'la' ? 'Los Angeles' : city === 'sf' ? 'San Francisco' : city;
        // Title-case the city for display/search
        detectedCity = normalized.replace(/\b\w/g, c => c.toUpperCase());
        break;
      }
    }

    // Detect content type preferences
    const contentMix: Record<string, number> = {};
    if (lowerQuery.includes('visual') || lowerQuery.includes('image') || lowerQuery.includes('art')) {
      contentMix.visual = 0.7;
    }
    if (lowerQuery.includes('text') || lowerQuery.includes('writing') || lowerQuery.includes('story')) {
      contentMix.text = 0.6;
    }
    if (lowerQuery.includes('people') || lowerQuery.includes('profile') || lowerQuery.includes('creator')) {
      contentMix.profiles = 0.8;
    }

    // Detect freshness requirements
    let freshnessHours: number | undefined;
    if (lowerQuery.includes('recent') || lowerQuery.includes('latest') || lowerQuery.includes('new')) {
      freshnessHours = 24;
    } else if (lowerQuery.includes('today')) {
      freshnessHours = 12;
    }

    // Smart weight calculation
    const weights = {
      semantic: 0.55,
      ts: detectedMood ? 0.25 : 0.2, // Higher text search for mood queries
      vibe: detectedMood ? 0.15 : 0.1,
      freshness: freshnessHours ? 0.2 : 0.1,
      resurface: 0.1
    };

    // Add exploration factor for creative queries
    const explorationKeywords = ['inspire', 'discover', 'explore', 'surprise'];
    const explore = explorationKeywords.some(keyword => lowerQuery.includes(keyword)) ? 0.3 : 0.1;

    // Synonym and concept expansion (keeps original tokens)
    const synonymBundles: Array<string[]> = [];
    const addBundleIf = (cond: boolean, bundle: string[]) => { if (cond) synonymBundles.push(bundle); };

    const has = (t: string) => lowerQuery.includes(t);

    // People/creator synonyms
    addBundleIf(has('artist') || has('artists'), ['artist','art','painter','illustrator','designer','creative','photography','photographer','visual']);

    // Exhibition/museum context
    addBundleIf(has('museum') || has('gallery') || has('exhibit') || has('exhibition'), ['museum','gallery','exhibit','exhibition','showcased','curator','retrospective','opening']);

    // Photo/media queries
    addBundleIf(has('photo') || has('photos') || has('pictures') || has('images') || has('photography'), ['photo','photos','picture','pictures','image','images','photography','shot','shoot']);

    // Nature/water example (e.g., "photos of water")
    addBundleIf(has('water') || has('ocean') || has('sea') || has('river') || has('lake'), ['water','ocean','sea','river','lake','waves','shore','beach']);

    // Build expanded q
    const originalTokens = Array.from(new Set(lowerQuery.split(/[^a-z0-9]+/).filter(Boolean)));
    const expanded = new Set<string>(originalTokens);
    for (const bundle of synonymBundles) {
      for (const term of bundle) expanded.add(term);
    }
    const expandedQ = Array.from(expanded).join(' ');

    return {
      q: expandedQ.trim() || query,
      mood: detectedMood,
      content_mix: Object.keys(contentMix).length > 0 ? contentMix : undefined,
      geo: detectedCity ? { type: 'city' as const, name: detectedCity } : (hasLocationIntent ? { type: 'city' as const } : undefined),
      freshness_hours: freshnessHours,
      weights,
      explore
    };
  }, []);

  // Enhanced LLM planner with intelligent retries and extended timeouts
  const buildPlanWithLLM = useCallback(async (query: string): Promise<SearchPlan | null> => {
    try {
      const { data, error } = await supabase.functions.invoke('simple-planner', {
        body: { 
          prompt: query
        }
      });

      if (error) {
        console.error('Simple planner error:', error);
        return null;
      }

      if (!data?.plan) {
        console.warn('No plan returned from simple planner');
        return null;
      }

      log.debug('Simple planner succeeded', { plan: data.plan });
      return data.plan as SearchPlan;
    } catch (error) {
      console.error('Simple planner failed:', error);
      return null;
    }
  }, [user?.id]);

  // Rate limiting check
  const checkRateLimit = useCallback((): boolean => {
    const now = Date.now();

    // Reset counter every minute
    if (now > rateLimitRef.current.resetTime) {
      rateLimitRef.current = { count: 0, resetTime: now + 60000 };
    }

    if (rateLimitRef.current.count >= MAX_SEARCHES_PER_MINUTE) {
      log.warn('Rate limit exceeded');
      toast({
        title: "Too many searches",
        description: "Please wait a moment before searching again.",
        variant: "destructive"
      });
      return false;
    }

    rateLimitRef.current.count++;
    return true;
  }, [toast]);

  // Main search function with smart caching and throttling
  const search = useCallback(async (query: string) => {
    // Prevent empty or duplicate searches
    if (!query.trim() || query === lastSearchRef.current) {
      return;
    }

    // Rate limiting
    if (!checkRateLimit()) {
      return;
    }

    // Throttle rapid searches
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(async () => {
      setIsSearching(true);
      setError(null);
      lastSearchRef.current = query;
      searchCountRef.current++;

      const searchId = searchCountRef.current;

      try {
        // Plan the search: try LLM planner first, then heuristic fallback
        const llmPlan = await buildPlanWithLLM(query);
        const plan = llmPlan ?? buildPlanHeuristic(query);
        log.info('Executing search', { query, plan, planner: llmPlan ? 'llm' : 'heuristic' });

        // Check cache first
        let cachedResult = searchCache.get(plan);

        // Try similar query if no exact match
        if (!cachedResult) {
          cachedResult = searchCache.findSimilarCached(plan);
          if (cachedResult) {
            log.info('Using similar cached result');
          }
        }

        if (cachedResult) {
          // Only update if this is still the latest search
          if (searchId === searchCountRef.current) {
            setResults(cachedResult);
            setIsSearching(false);
          }
          return;
        }

        // Execute hybrid search with enhanced parameters
        console.log('Invoking hybrid-search with plan:', plan);
        const { data, error: searchError } = await supabase.functions.invoke('hybrid-search', {
          body: {
            plan,
            limit: 20,
            user_id: user?.id
          }
        });

        console.log('Hybrid search response:', { data, searchError });

        if (searchError) {
          console.error('Search error details:', searchError);
          throw new Error(searchError.message || 'Search failed');
        }

        // Track search in context memory
        await trackSearchQuery(
          query, 
          data?.total_results || 0, 
          plan.mood
        );

        // Cache successful results
        if (data) {
          searchCache.set(plan, data);
        }

        // Only update if this is still the latest search
        if (searchId === searchCountRef.current) {
          setResults(data || { results: [], total_results: 0, constellation_id: null, plan_used: plan, processing_time: 0 });
          setIsSearching(false);
        }

      } catch (err) {
        log.error('Search error:', err);

        // Only update if this is still the latest search
        if (searchId === searchCountRef.current) {
          setError(err instanceof Error ? err.message : 'Search failed');
          setIsSearching(false);

          toast({
            title: "Search failed",
            description: "Unable to search right now. Please try again.",
            variant: "destructive"
          });
        }
      }
    }, 300); // 300ms throttle
  }, [buildPlanHeuristic, buildPlanWithLLM, checkRateLimit, toast]);

  // Clear search results
  const clearResults = useCallback(() => {
    setResults(null);
    setError(null);
    lastSearchRef.current = '';

    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Cache stats for debugging
  const getCacheStats = useCallback(() => {
    return searchCache.getStats();
  }, []);

  // Get performance metrics (placeholder for now)
  const getPerformanceMetrics = useCallback(() => {
    return null;
  }, []);

  return {
    search,
    clearResults,
    isSearching,
    results,
    error,
    getCacheStats,
    getPerformanceMetrics,
    trackContentInteraction
  };
};